import React, { useState, useCallback } from "react";
import { format, subDays, startOfDay, endOfDay } from "date-fns";
import { useHotel } from "../../contexts/HotelContext";
import Layout from "../../components/Layout";
import { useFormGenerationsDashboard } from "../../hooks/useFormGenerationsDashboard";
import { useFormGenerationsExport } from "../../hooks/useFormGenerationsExport";
import {
  DateRangePicker,
  ExportButton,
  ErrorState,
  AccessDeniedState,
} from "../../components/admin/llm-usage";
import {
  FormGenerationSummaryCards,
  DailyTrendChart,
  BreakdownTables,
  FormGenerationsLoadingSkeleton,
} from "../../components/admin/form-generations";

/**
 * Format date as YYYY-MM-DD string (no timezone conversion)
 */
const formatDateOnly = (date: Date): string => {
  return format(date, "yyyy-MM-dd");
};

const getDefaultDateRange = () => {
  const now = new Date();
  const start = startOfDay(subDays(now, 30));
  const end = endOfDay(now);
  return {
    dateFrom: formatDateOnly(start),
    dateTo: formatDateOnly(end),
  };
};

const FormGenerations: React.FC = () => {
  const { user, hasModuleEnabled } = useHotel();

  // Date range state
  const [dateRange, setDateRange] = useState(getDefaultDateRange);

  // Query hook
  const {
    data,
    isLoading,
    isRefetching,
    error,
    refetch,
  } = useFormGenerationsDashboard({
    dateFrom: dateRange.dateFrom,
    dateTo: dateRange.dateTo,
  });

  // Export hook
  const {
    exportCsv,
    isLoading: isExporting,
    error: exportError,
    isRateLimited,
    remainingExports,
    countdown,
  } = useFormGenerationsExport();

  // Check access — mirror ModeratorRoute: global admin OR kiedynabory
  // moderator module. Tenant-level grants:schedule is enforced by the
  // backend @RequiredModule('grants:schedule') ModuleGuard.
  const canModerate =
    user?.role === "admin" || hasModuleEnabled("admin:kiedynabory:write");

  // Handle date change - DateRangePicker sends startDate/endDate, we convert to dateFrom/dateTo
  const handleDateChange = useCallback((startDate: string, endDate: string) => {
    setDateRange({ dateFrom: startDate, dateTo: endDate });
  }, []);

  // Handle export
  const handleExport = useCallback(() => {
    exportCsv({
      dateFrom: dateRange.dateFrom,
      dateTo: dateRange.dateTo,
    });
  }, [exportCsv, dateRange]);

  // Redirect users without moderator access
  if (!canModerate) {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 text-left">
              Monitoring generacji de minimis
            </h1>
          </div>
          <AccessDeniedState message="Ta strona jest dostępna tylko dla administratorów z dostępem do modułu grants:schedule." />
        </div>
      </Layout>
    );
  }

  // Determine if data is empty
  const isEmpty = data && data.summary.totalGenerations === 0;

  return (
    <Layout>
      <div className="relative space-y-6" data-testid="form-generations-page" aria-busy={isRefetching}>
        {/* Refetch Overlay */}
        {isRefetching && !isLoading && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10" role="status" aria-label="Odświeżanie danych">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">
              Monitoring generacji de minimis
            </h1>
            <p className="text-neutral-600 mt-1">
              Monitoruj generacje formularzy de minimis per organizacja i
              użytkownik
            </p>
          </div>

          <div>
            <ExportButton
              onClick={handleExport}
              isLoading={isExporting}
              isRateLimited={isRateLimited}
              remainingExports={remainingExports}
              countdown={countdown}
              disabled={isLoading || !!error || isEmpty}
            />
            {exportError && (
              <p className="text-red-500 text-sm mt-1" role="alert">
                Błąd eksportu: {exportError.message}
              </p>
            )}
          </div>
        </div>

        {/* Date Range Picker */}
        <div className="bg-white rounded-lg border border-neutral-200 p-4">
          <DateRangePicker
            startDate={dateRange.dateFrom}
            endDate={dateRange.dateTo}
            onDateChange={handleDateChange}
            maxDays={365}
          />
        </div>

        {/* Loading State */}
        {isLoading && <FormGenerationsLoadingSkeleton />}

        {/* Error State */}
        {error && <ErrorState error={error} onRetry={() => refetch()} />}

        {/* Empty State */}
        {!isLoading && !error && isEmpty && (
          <div className="bg-white rounded-lg border border-neutral-200 p-12 text-center" data-testid="empty-state">
            <p className="text-neutral-500 text-lg">Brak danych o generacjach formularzy dla wybranego zakresu dat</p>
          </div>
        )}

        {/* Dashboard Content */}
        {!isLoading && !error && data && !isEmpty && (
          <>
            {/* Summary Cards */}
            <FormGenerationSummaryCards summary={data.summary} />

            {/* Daily Trend Chart */}
            <DailyTrendChart data={data.byDay} />

            {/* Breakdown Tables */}
            <BreakdownTables
              byTenant={data.byTenant}
              byUser={data.byUser}
            />
          </>
        )}
      </div>
    </Layout>
  );
};

export default FormGenerations;
